import { t } from '@kc-console/core';

import {
  type Notification,
  NotificationCategory,
  type NotificationResource,
  NotificationStatus,
} from '../constants/types/notification';

const NOTIFICATION_CATEGORY_LABEL_KEY: Record<NotificationCategory, string> = {
  [NotificationCategory.OPERATION]: 'layout:notification.category.resource.label',
  [NotificationCategory.RESOURCE_LIFECYCLE]: 'layout:notification.category.resource.label',
  [NotificationCategory.PLATFORM_NOTICE]: 'layout:notification.category.platform_notice.label',
};

export type NotificationCountLabel = {
  color: 'green' | 'red';
  count: number;
  key: 'success' | 'error';
  label: string;
};

const pad2 = (value: number): string => String(value).padStart(2, '0');

/**
 * 알림 시각을 상대시간으로 표기할 최대 경과일.
 * @description
 * - BFF 가 알림을 생성 시각 + 8일 기준으로 배치 삭제하므로, 실제 노출되는 최대 경과일은 7일입니다.
 * - 이 값이 BFF 보관 기간보다 짧으면 아직 노출 중인 알림이 상대시간 대신 날짜 형식으로 떨어집니다. (KIC-59982)
 */
const RELATIVE_TIME_MAX_DAYS = 7;

export const formatNotificationRelativeTime = (occurredAt: string | undefined | null): string => {
  if (!occurredAt) return '';

  const occurred = new Date(occurredAt);
  const occurredMs = occurred.getTime();
  if (Number.isNaN(occurredMs)) return '';

  const diffSec = Math.floor((Date.now() - occurredMs) / 1000);
  if (diffSec < 60) return t('layout:notification.time.just_now');

  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return t('layout:notification.time.minutes_ago', { elapsedMinutes: diffMin });

  const diffHour = Math.floor(diffMin / 60);
  if (diffHour < 24) return t('layout:notification.time.hours_ago', { elapsedHours: diffHour });

  const diffDay = Math.floor(diffHour / 24);
  if (diffDay <= RELATIVE_TIME_MAX_DAYS)
    return t('layout:notification.time.days_ago', { elapsedDays: diffDay });

  return `${occurred.getFullYear()}-${pad2(occurred.getMonth() + 1)}-${pad2(occurred.getDate())}`;
};

export const getNotificationCategoryLabel = (category: NotificationCategory): string =>
  t(NOTIFICATION_CATEGORY_LABEL_KEY[category]);

export const getNotificationResourceCounts = (resources: NotificationResource[] = []) => {
  const successCount = resources.filter(
    resource => resource.status === NotificationStatus.SUCCESS,
  ).length;
  const errorCount = resources.filter(
    resource => resource.status === NotificationStatus.ERROR,
  ).length;

  return {
    errorCount,
    successCount,
  };
};

export const hasMultipleNotificationResources = (notification: Pick<Notification, 'resources'>) =>
  (notification.resources ?? []).length >= 2;

export const getNotificationCountLabels = (
  notification: Pick<Notification, 'resources'>,
): NotificationCountLabel[] => {
  if (!hasMultipleNotificationResources(notification)) return [];

  const { errorCount, successCount } = getNotificationResourceCounts(notification.resources ?? []);
  const labels: NotificationCountLabel[] = [];

  if (successCount > 0) {
    labels.push({
      color: 'green',
      count: successCount,
      key: 'success',
      label: `성공 ${successCount}`,
    });
  }

  if (errorCount > 0) {
    labels.push({
      color: 'red',
      count: errorCount,
      key: 'error',
      label: `실패 ${errorCount}`,
    });
  }

  return labels;
};

export const getNotificationDisplayMeta = (
  notification: Pick<Notification, 'category' | 'occurredAt'>,
) => ({
  categoryLabel: getNotificationCategoryLabel(notification.category),
  time: formatNotificationRelativeTime(notification.occurredAt),
});
